import { Global, ThemeProvider, css } from "@emotion/react";
import { useState, type ReactNode } from "react";
import { HdrHighlights } from "./HdrHighlights";
import { theme } from "../styles/theme";
import { TimezoneContext, type Timezone } from "./TimezoneContext";
import { MatchdaySoundProvider } from "./MatchdaySound";

const foundations = css`
  @font-face {
    font-family: "Barlow Condensed";
    src: url("/fonts/barlowCondensedBold.ttf") format("truetype");
    font-weight: 700;
    font-display: swap;
  }
  * {
    box-sizing: border-box;
  }
  html {
    scroll-behavior: auto;
    color-scheme: dark;
    scroll-padding-top: 110px;
  }
  body {
    margin: 0;
    font-family: ${theme.font.body};
    color: ${theme.colors.text};
    background: ${theme.colors.background};
    font-size: 16px;
    line-height: 1.65;
  }
  h1,
  h2,
  h3,
  h4,
  p {
    margin-top: 0;
  }
  h1,
  h2,
  h3,
  h4 {
    font-family: ${theme.font.display};
    font-weight: 700;
    line-height: 1.06;
  }
  h1 {
    font-size: clamp(2.8rem, 6.1vw, 5.5rem);
    letter-spacing: -0.02em;
  }
  h2 {
    font-size: clamp(2rem, 3.2vw, 3rem);
  }
  h3 {
    font-size: 1.75rem;
  }
  p {
    margin-bottom: 1rem;
  }
  a {
    color: inherit;
    text-underline-offset: 0.25em;
  }
  button,
  input,
  select,
  textarea {
    font: inherit;
  }
  button,
  select {
    cursor: pointer;
  }
  button:disabled,
  input:disabled,
  select:disabled,
  textarea:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  a,
  button,
  summary,
  input,
  select,
  textarea {
    transition:
      color 160ms,
      background-color 160ms,
      border-color 160ms,
      text-decoration-color 160ms,
      filter 160ms,
      opacity 160ms;
  }
  a:where(:hover, :focus-visible) {
    color: ${theme.colors.gold};
    text-decoration: underline;
    text-decoration-thickness: 2px;
  }
  a:active {
    opacity: 0.82;
  }
  @media (hover: hover) and (pointer: fine) {
    :where(button:enabled:hover) {
      filter: brightness(1.1);
    }
    :where(summary:hover) {
      background-color: #e8cd7308;
      color: ${theme.colors.gold};
    }
  }
  :where(button:enabled:active) {
    filter: brightness(0.94);
  }
  :where(summary:active) {
    background-color: #e8cd7314;
  }
  button,
  a,
  summary,
  input,
  select {
    -webkit-tap-highlight-color: transparent;
  }
  :focus-visible {
    outline: 3px solid ${theme.colors.gold};
    outline-offset: 5px;
  }
  img,
  svg {
    max-width: 100%;
  }
  img {
    display: block;
  }
  a,
  button,
  input,
  select,
  textarea,
  summary {
    border-radius: 0;
  }
  button svg,
  a svg {
    flex-shrink: 0;
  }
  ::selection {
    background: ${theme.colors.gold};
    color: ${theme.colors.deep};
  }
  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }
    *,
    *::before,
    *::after {
      animation: none !important;
      transition: none !important;
    }
  }
`;

export function AppProviders({ children }: { children: ReactNode }) {
  const [zone, setZone] = useState<Timezone>("WAT");
  return (
    <ThemeProvider theme={theme}>
      <TimezoneContext.Provider value={{ zone, setZone }}>
        <Global styles={foundations} />
        <HdrHighlights />
        <MatchdaySoundProvider>{children}</MatchdaySoundProvider>
      </TimezoneContext.Provider>
    </ThemeProvider>
  );
}
